import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { AuthContext, ProfileContext } from '../../App';
import { getCachedDeviceInfo } from '../../util/deviceCheck';
import { postForm } from '../../util/HttpRequest';
import { base64ToBlob } from '../../util/ImageProcessor';
import Waiting from '../Waiting';
import ClaimRemoteDepositCheckCapturePreview from './RemoteDepositCheckCapturePreview';
import { RemoteDepositCheckCaptureProps, ExtractClaimCheckResponse, Orientation } from '../../types/RemoteDepositCheck.types';
import useScreenOrientation from '../../util/useScreenOrientation';

function ClaimRemoteDepositCheckCapture({ onExtracted, onNext, toggleModal }: RemoteDepositCheckCaptureProps) {
    const { accessToken, extendSession, user } = useContext(AuthContext);
    const { userProfileLossDraftsClaim } = useContext(ProfileContext);
    const [processing, setProcessing] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [validateErrorMsg, setValidateErrorMsg] = useState("");
    const [croppedFrontImageSrc, setCroppedFrontImageSrc] = useState(""); //base64 image string. Cropped front image used for user check image preview.
    const [croppedRearImageSrc, setCroppedRearImageSrc] = useState("");   //base64 image string. Cropped rear image used for user check image preview.  
    const [captureFrontImage, setCaptureFrontImage] = useState(false);
    const [captureRearImage, setCaptureRearImage] = useState(false);
    const [fullScreenFailed, setFullScreenFailed] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const rawCheckFrontImageRef = useRef("");
    const rawCheckRearImageRef = useRef("");
    const screenOrientation = useRef<Orientation | null>(null);
    const { t, i18n } = useTranslation(["Form", "ClaimRemoteDepositCheck"]);
    const translatedAutoCaptureEventHints = i18n.getResource(i18n.language, 'ClaimRemoteDepositCheck', 'autoCaptureEvents');
    const { model, platform, isFirefox } = getCachedDeviceInfo();

    // useCallback ensures that the function instance is stable across renders (unless dependencies change)
    // This callback is triggered by useScreenOrientation when screen orientation changes
    // It refreshes or re-injects the iframe to properly render it in the new orientation. (but only used for non-Android devices)
    //For non-android device the camera will not function in portrait mode, the user will see a warning message to change the orientation.
    //camera display will be triggered once the user chnages the screen orienation to landscape.
    // For android devices when the user clicks the camera icon , it programatically change the screen orientation to landscape mode and it will be locked
    // and the camera dispaly will be openend in full screen. 
    //Both full screen and screen orientation lock/unlock API not supported for apple devices

    const handleOrientationChange = useCallback((newOrientation: Orientation) => {
        reloadIframe(); // Force iframe reload on orientation change to start/stop camera
    }, []);
    screenOrientation.current = useScreenOrientation(handleOrientationChange); //Hook for orientation chamge

    useEffect(() => {
        function handleMessageFromMiSnap(event: MessageEvent) {
            if (platform === 'android' && !isFirefox && event.data?.event === 'AUTOCAPTURE_ENDED') {
                closeFullScreen(); // Only exit fullscreen for Android
            }
        }
        // Add event listener for messages from the iframe to know the autocapture is success to exit from fullscreen
        window.addEventListener("message", handleMessageFromMiSnap);
        return () => { //clean up function
            window.removeEventListener("message", handleMessageFromMiSnap);
        };
    }, []);

    function haveFrontImage() {
        return croppedFrontImageSrc > '';
    }

    function haveRearImage() {
        return croppedRearImageSrc > '';
    }

    function setDocumentType(captureType: string) {
        if (captureType === 'f') {
            setCaptureFrontImage(true);
            setCaptureRearImage(false);
        }
        else if (captureType === 'b') {
            setCaptureFrontImage(false);
            setCaptureRearImage(true);
        }
        else {
            setCaptureFrontImage(false);
            setCaptureRearImage(false);
        }
    }

    function getDocumentType(friendly: boolean) {
        if (captureFrontImage) {
            return friendly ? t('ClaimRemoteDepositCheck:lblcaptureCheckFrontImage') : "CHECK_FRONT";
        }
        else if (captureRearImage) {
            return friendly ? t('ClaimRemoteDepositCheck:lblcaptureCheckRearImage') : "CHECK_BACK";
        }
        else {
            return "";
        }
    }

    async function iframeOnLoad(evt: any, documentType: string) {
        let iframe = evt.target as HTMLIFrameElement;
        if (iframe.contentDocument) { // Ensure contentDocument is available
            // set documenttype (front or rear) data attribute in iframe
            let iframeResultImage = iframe.contentDocument.getElementById('resultImage') as HTMLImageElement;
            iframeResultImage.dataset.documenttype = documentType;

            const isPortrait = screenOrientation.current?.includes('portrait');
            const isLandscape = screenOrientation.current?.includes('landscape');
           
            if (platform === 'android' && !isFirefox) { //Screen orienation API lock/unlock not supportd in firefox
                const permissionStatus = await requestCameraAccessIfPrompted();

                if (!permissionStatus || permissionStatus.state === 'denied') {
                    handleAutoCapture(iframe); // Let SDK handle denied message
                    return;
                }
                if (permissionStatus.state === 'granted') {
                    captureFullScreenMode(iframe);
                    toggleScreenOrientation(true);  // Lock to landscape
                }
            }
            else { // apple devices, android firefox browsers and other unknown devices
                if (isPortrait) {
                   //If you're using t outside a React component (in a utility or plain function), make sure you're importing it from the initialized instance:
                   const translatedMessage = i18n.t('ClaimRemoteDepositCheck:iframePortraitModeWarning');
                   showIframeWarningMessage(iframe,translatedMessage);
                }
                else if (isLandscape) {
                    handleAutoCapture(iframe);
                }
            }
        }
    }

    async function requestCameraAccessIfPrompted(): Promise<PermissionStatus | null> {
        let permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        if (permissionStatus.state === 'prompt') {
            try {
                await navigator.mediaDevices.getUserMedia({ video: true }); // Trigger permission prompt
                // update the state
                permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
            } catch (err) {
                console.warn("User denied camera access during prompt:", err);
                return null;
            }
        }
        return permissionStatus;
    }

    function handleAutoCapture(iframe: HTMLIFrameElement) {
        const autoCaptureBtn = iframe.contentDocument?.getElementById('autoCaptureButton') as HTMLButtonElement;
        autoCaptureBtn.click();
    }

    function showIframeWarningMessage(iframe: HTMLIFrameElement, message: string) {
        const warningDiv = iframe.contentDocument?.getElementById('warning') as HTMLDivElement;
        if (warningDiv) {
            warningDiv?.classList.remove('hidden-element');
            warningDiv.innerHTML = message; //shows the message in misnap.html page
        }
    }

    function reloadIframe() {
        const iframe = iframeRef.current;
        if (iframe?.contentDocument) {
            const resultImage = iframe.contentDocument.getElementById('resultImage') as HTMLImageElement | null;
            // Check if the result image exists and has a non-empty raw image URL ie misnap captured a successful check image
            const hasRawImage = resultImage?.dataset?.rawimageurl && resultImage.dataset.rawimageurl.trim() !== "";
            if (!hasRawImage) {
                // Only reload if there's no valid captured image
                setIframeKey(prev => prev + 1);
            }
        }
    }

    async function captureFullScreenMode(iframe: HTMLIFrameElement) {
        try {
            if (iframe.requestFullscreen) {
                if (fullScreenFailed) {
                    setFullScreenFailed(false); // Reset the failure state to show the Retake button instaed of Start camera button
                }
                await iframe.requestFullscreen();
                handleAutoCapture(iframe);
            }
        }
        catch (err) {
            setFullScreenFailed(true);
            //If you're using t outside a React component (in a utility or plain function), make sure you're importing it from the initialized instance.
            const translatedMessage = i18n.t('ClaimRemoteDepositCheck:iframeFullScreenFailureWarning');
            showIframeWarningMessage(iframe, translatedMessage);
        }
    }

    function toggleScreenOrientation(lock: boolean) {
        const orientation = screen.orientation as any;

        if (!orientation) {
            console.warn("Screen Orientation API is not supported on this browser.");
            return;
        }

        if (lock) {
            if (orientation.lock) {
                orientation.lock("landscape").catch((err: any) => {
                    console.warn("Orientation lock failed:", err);
                });
            } else {
                console.warn("Orientation lock is not supported.");
            }
        } else {
            if (orientation.unlock) {
                orientation.unlock();
            } else {
                console.warn("Orientation unlock is not supported.");
            }
        }
    }

    function closeFullScreen() {
        if (document.exitFullscreen) {
            document.exitFullscreen().catch(err => console.warn("Error exiting fullscreen:", err));
        } else {
            console.warn("Fullscreen exit not supported in this browser.");
        }
        if (platform === 'android' && !isFirefox) { //Screen orienation API lock/unlock not supportd in firefox
            toggleScreenOrientation(false); // Reset/unlock orientation
        }
    }

    function iframeOnCancel() {
        setDocumentType('');
    }

    function iframeOnRetake() {
        extendSession(); // Extend the user's session to prevent timeout while re-taking a check image.
        setIframeKey(iframeKey + 1);
    }

    function iframeOnSubmit(evt: any) {
        let iframe = iframeRef.current;
        if (iframe && iframe.contentDocument) {
            let iframeResultImage = iframe.contentDocument.getElementById('resultImage') as HTMLImageElement;
            if (iframeResultImage && iframeResultImage.src) {
                if (captureFrontImage) {
                    setCroppedFrontImageSrc(iframeResultImage.src);
                    rawCheckFrontImageRef.current = iframeResultImage.dataset.rawimageurl || ""; // Original captured image without cropping. Ensures it's always a base64 string
                }
                else if (captureRearImage) {
                    setCroppedRearImageSrc(iframeResultImage.src);
                    rawCheckRearImageRef.current = iframeResultImage.dataset.rawimageurl || ""; // Original captured image without cropping. Ensures it's always a base64  string
                }
                setDocumentType('');
            }
        }
    }

    async function handleExtractClaimCheckInfo() {
        setProcessing(true);
        setIsDisabled(true);
        extendSession();
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ExtractClaimCheckInfo");
        const formData = new FormData();
        formData.append("ClaimId", userProfileLossDraftsClaim.claimId.toString());
        formData.append("UserId", user.id);
        formData.append("DeviceDescription", model);
        formData.append("CheckFrontImage", base64ToBlob(rawCheckFrontImageRef.current, "image/jpeg"));
        formData.append("CheckRearImage", base64ToBlob(rawCheckRearImageRef.current, "image/jpeg"));

        console.log("Extracting Claim Check Info (Streaming) to " + url);

        postForm(url, accessToken, formData)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.status + " - " + response.statusText)
                }
                return response.json();
            })
            .then((response: ExtractClaimCheckResponse) => {
                onExtracted(response);
                onNext();
            })
            .catch((err) => {
                setValidateErrorMsg(t('ClaimRemoteDepositCheck:checkExtractionFailure'));
                console.log("Error occuring while calling handleExtractClaimCheckInfo(): ", err.message);
            })
            .finally(() => {
                setProcessing(false);
            })
    };

    function renderPreCaptureForm() {
        return (
            <>
                <div>
                    <ul>
                        <li>{t('ClaimRemoteDepositCheck:instructionsLI1')}</li>
                        <li>{t('ClaimRemoteDepositCheck:instructionsLI2')}</li>
                        <li>{t('ClaimRemoteDepositCheck:instructionsLI3')}</li>
                        <li>{t('ClaimRemoteDepositCheck:instructionsLI4')}</li>
                        <li>{t('ClaimRemoteDepositCheck:instructionsLI5')}</li>
                    </ul>
                </div>
                <div className='row-container'>
                    <div className='column-container space-evenly column-gap'>
                        <div>
                            <ClaimRemoteDepositCheckCapturePreview
                                labelId="lblPreviewFront"
                                labelText={t('ClaimRemoteDepositCheck:lblCheckFront')}
                                hasImage={haveFrontImage()}
                                imageSrc={croppedFrontImageSrc}
                                onClick={() => setDocumentType('f')}
                                altText={t('ClaimRemoteDepositCheck:checkFrontImageAlternateText')}
                            />
                        </div>
                        <div>
                            <ClaimRemoteDepositCheckCapturePreview
                                labelId="lblPreviewRear"
                                labelText={t('ClaimRemoteDepositCheck:lblCheckRear')}
                                hasImage={haveRearImage()}
                                imageSrc={croppedRearImageSrc}
                                onClick={() => setDocumentType('b')}
                                altText={t('ClaimRemoteDepositCheck:checkRearImageAlternateText')}
                            />
                        </div>
                    </div>
                </div>
                <br /><br />
                <Button type="default" className="button" onClick={() => toggleModal(false)}>
                    {t('Form:btnCancel')}
                </Button>
                &nbsp;&nbsp;
                <Button type="primary" className="button submit" onClick={() => handleExtractClaimCheckInfo()} disabled={isDisabled || !(haveFrontImage() && haveRearImage())}>
                    {t('Form:btnValidate')}
                </Button>
                <br /><br />
                {
                    processing && (<span>{t('ClaimRemoteDepositCheck:lblProcessingValidate')} &nbsp;<Waiting /></span>)
                }
                <div className="error">{validateErrorMsg}</div>
            </>
        );
    }

    function renderCaptureImageForm() {
        return (
            <>
                <div>
                    <h1>{getDocumentType(true)}</h1>
                    <iframe
                        src="../misnap.html"
                        width={350}
                        height={325}
                        title={t('ClaimRemoteDepositCheck:lblIframeTitle')}
                        loading="lazy"
                        id="misnapFrame"
                        ref={iframeRef}
                        key={iframeKey}
                        data-translatedautocaptureeventhints={JSON.stringify(translatedAutoCaptureEventHints)}
                        onLoad={(evt) => iframeOnLoad(evt, getDocumentType(false))}
                        sandbox="allow-same-origin allow-scripts allow-orientation-lock"
                        allow="fullscreen;"
                        style={{
                            width: "100%",
                            border: "none",
                        }}
                    />
                </div>
                <br />
                <Button type="default" className="button" onClick={() => iframeOnCancel()}>{t('Form:btnCancel')}</Button>
                &nbsp;&nbsp;
                <Button type="default" className="button"  id="iframeReStartButton"onClick={() => iframeOnRetake()} style={{ display: fullScreenFailed ? 'inline-block' : 'none' }}>
                    {t('ClaimRemoteDepositCheck:btnStartCamera')}
                </Button> 
                  {/* </Button> button will be available in the dom because misnap use its reference */}
                <Button type="default" className="button" id="iframeRetakeButton"onClick={() => iframeOnRetake()} style={{ display: fullScreenFailed ? 'none' : 'inline-block' }}>
                    {t('ClaimRemoteDepositCheck:btnReTake')}
                </Button> 
                &nbsp;&nbsp;
                <Button type="primary" className="button submit" id="iframeSubmitButton" onClick={(evt) => iframeOnSubmit(evt)}>
                    {t('ClaimRemoteDepositCheck:btnLooksGood')}
                </Button>
                <br /><br />
            </>
        );
    }

    return (
        <>
            {(!captureFrontImage && !captureRearImage) && renderPreCaptureForm()}
            {(captureFrontImage || captureRearImage) && renderCaptureImageForm()}
        </>
    );
};
export default ClaimRemoteDepositCheckCapture;